"use client";
import { useEffect, useMemo, useState } from "react";
import {
  getDisplayedCourses,
  getIndividualCourses,
  getProgramCourses,
  hasProgram,
} from "@/components/Navigation/multiCart/utils";
import { Button } from "@/components/ui/button";
import CartSuggestions from "./CartSuggestions";
import makeEvent from "@/hooks/eventing/makeEvent";
import { ConversionTracked } from "@/hooks/eventing/types/core.conversions.ConversionTracked.v2";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { EVENTS } from "@/hooks/eventing/types/events";
import { CommercePath, useBuildCommerceUrl } from "@/hooks/useBuildCommerceUrl";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import MonarchResolver from "@/monarch/MonarchResolver";
import type { AbandonedCartTestResult } from "@/monarch/rules/abandonedCart/types";
import type { AbandonedCartIntentMatchTestResult } from "@/monarch/rules/abandonedCartIntentMatch/types";
import {
  CartData,
  firstEnrichedProgramInfo,
  formatCurrency,
} from "@/utils/cartUtils";
import { getCorrelationID } from "@/utils/tagular/helpers";
import {
  formatCourseName,
  createProductViewEventData,
  createElementViewEventData,
  createConversionEventData,
  ABANDONED_CART_EVENT_CONFIGS,
} from "./utils";
interface CartPopupProps {
  isOpen: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  cartData: CartData;
}

export default function CartPopup({
  isOpen,
  onMouseEnter,
  onMouseLeave,
  cartData,
}: CartPopupProps) {
  const [showAllCourses, setShowAllCourses] = useState<Record<string, boolean>>(
    {},
  );

  const items = useMemo(() => cartData?.items ?? [], [cartData]);
  const subtotal = useMemo(() => cartData?.subtotal ?? 0, [cartData]);
  const total = useMemo(() => cartData?.total ?? 0, [cartData]);
  const hasProgramItems = hasProgram(items);
  const individualCourses = getIndividualCourses(items, cartData.bundleInfoMap);
  const checkoutUrl = useBuildCommerceUrl(CommercePath.PaymentRedirect);

  // Brand information comes from CommerceTools lineItems[].variant.attributes['brand-text']
  // For now using mock brand data, will be replaced with real CommerceTools data

  // Use Tagular's dedicated correlation ID system for conversion tracking
  const correlationId = getCorrelationID();

  // Element event tracking for checkout CTA - no static webElement, will use dynamic text
  const { elementClicked: checkoutCtaClicked } = useElementEvents({
    webElement: ABANDONED_CART_EVENT_CONFIGS.cartPopupButton,
  });

  useEffect(() => {
    if (isOpen && items.length > 0) {
      // Fire events for each item using makeEvent (simpler approach)
      items.forEach((item, index) => {
        // Product Viewed event
        const productViewedEvent = makeEvent(
          createProductViewEventData(
            item,
            index,
            cartData?.enrichedProgramInfo[item.bundleId ?? ""]?.brand,
            correlationId,
          ),
        );
        productViewedEvent(EVENTS.ProductViewed)();

        // Element Viewed event
        const elementViewedEvent = makeEvent(
          createElementViewEventData(item, index, correlationId),
        );
        elementViewedEvent(EVENTS.ElementViewed)();
      });
    }
  }, [isOpen, items, cartData?.enrichedProgramInfo, correlationId]);

  const programInfo = firstEnrichedProgramInfo(cartData);

  // Track conversion when going to checkout
  const handleCheckoutClick = (buttonText: string) => {
    // Track element click with dynamic text
    checkoutCtaClicked({
      correlationId,
      webElement: {
        ...ABANDONED_CART_EVENT_CONFIGS.cartPopupButton,
        text: buttonText, // Use dynamic text from Monarch
      },
    });

    // Track conversion using Tagular correlation ID
    const conversionEvent = createConversionEventData(
      items,
      programInfo?.brand,
      correlationId,
      "cart_checkout",
    );

    const event = makeEvent<ConversionTracked, EVENTS>(conversionEvent);
    event<ConversionTracked>(EVENTS.ConversionTracked)();

    // Add small delay to ensure event is sent before navigation
    setTimeout(() => {
      window.location.href = checkoutUrl;
    }, 150);
  };

  if (!isOpen) return null;

  return (
    <div
      className={[
        "bg-white border border-gray-200 shadow-lg z-50 rounded",

        // Desktop/tablet: anchored to the cart icon
        "md:absolute md:right-0 md:top-full md:mt-2",

        // Mobile: center it and keep it inside the viewport
        "fixed md:absolute left-1/2 md:left-auto -translate-x-1/2 md:translate-x-0 top-16 md:top-auto",

        // Widths: clamp to viewport on mobile; keep original widths on md+
        items.length === 0
          ? "md:min-w-52 md:w-auto w-[clamp(14rem,92vw,22rem)]"
          : "md:w-96 w-[clamp(18rem,92vw,26rem)]",

        // Safety: never exceed viewport width
        "max-w-[calc(100vw-1rem)]",

        // Margin for mobile gutters
        "mx-2 md:mx-0",

        // Cap overall height and clip extra - scrolling for entire modal
        "max-h-[85vh] overflow-y-auto overflow-x-hidden overscroll-contain",
      ].join(" ")}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ zIndex: "calc(infinity)" }}
    >
      <div className={items.length === 0 ? "px-4" : "p-4 md:p-6"}>
        <div
          className={`cart-section w-full cart-section-styled flex flex-col justify-center ${
            items.length === 0 ? "min-h-[80px] text-center" : "min-h-[200px]"
          }`}
        >
          <h2
            className={`font-bold ${items.length === 0 ? "text-left mb-1 mt-0 text-[20px]" : "mb-3 mt-0 text-lg"}`}
          >
            {items.length === 0 ? "Your cart is empty" : "In your cart"}
          </h2>
          {items.length > 0 && (
            <>
              <div className="border-b border-gray-200 mb-2"></div>

              {/* Scrollable LIST ONLY */}
              <div className="flex-1 space-y-4 mb-6 pr-2">
                {/* Show Programs first if they exist */}
                {hasProgramItems &&
                  Object.entries(cartData.bundleInfoMap).map(
                    ([bundleId, bundleInfo]) => {
                      if (!bundleId) return null;
                      const programCourses = getProgramCourses(items, bundleId);
                      const isShowingAll =
                        showAllCourses[bundleId || ""] || false;
                      const displayedCourses = getDisplayedCourses(
                        items,
                        bundleId,
                        showAllCourses,
                      );
                      const totalPrice = programCourses.reduce(
                        (n, { totalPrice, price }) =>
                          totalPrice ? n + totalPrice : n + price,
                        0,
                      );
                      const originalPrice = programCourses.reduce(
                        (n, { originalPrice, price }) =>
                          originalPrice ? n + originalPrice : n + price,
                        0,
                      );

                      return (
                        <div
                          key={bundleId}
                          className="flex justify-between items-start"
                        >
                          <div className="flex-1">
                            <p className="font-semibold text-black mb-0 mt-3 text-base font-inter">
                              {formatCourseName(
                                bundleInfo.title,
                                bundleInfo.partners[0]?.key,
                              )}
                            </p>
                            <div className="flex justify-between items-center mt-1">
                              <span className="text-gray-600 text-base">
                                Verified Certificate
                              </span>
                              <div>
                                {originalPrice > totalPrice && (
                                  <span className="text-gray-500 line-through text-sm mr-2">
                                    {formatCurrency(originalPrice)}
                                  </span>
                                )}
                                <span className="text-black text-base font-normal">
                                  {formatCurrency(totalPrice)}
                                </span>
                              </div>
                            </div>
                            <p className="text-black text-sm mt-2 mb-2 font-bold">
                              This program includes {programCourses.length}{" "}
                              course{programCourses.length > 1 ? "s" : ""}:
                            </p>
                            {programCourses.length > 0 && (
                              <button
                                onClick={() =>
                                  setShowAllCourses((prev) => ({
                                    ...prev,
                                    [bundleId || ""]: !isShowingAll,
                                  }))
                                }
                                className="text-[#00688D] underline text-sm flex items-center mb-2"
                              >
                                {isShowingAll ? "Show less" : "Show all"}
                                <span className="ml-1 text-black no-underline">
                                  {isShowingAll ? (
                                    <svg
                                      className="w-5 h-5"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
                                    </svg>
                                  ) : (
                                    <svg
                                      className="w-5 h-5"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
                                    </svg>
                                  )}
                                </span>
                              </button>
                            )}
                            <div className="flex flex-col gap-1">
                              {displayedCourses.map((course, index) => (
                                <div
                                  key={course.id || index}
                                  className="flex items-start"
                                >
                                  <span className="text-black mr-2">•</span>
                                  <span className="text-black text-sm">
                                    {formatCourseName(course.courseName)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    },
                  )}

                {/* Show Individual Courses */}
                {individualCourses.map((item) => (
                  <div
                    key={item.id}
                    className="flex justify-between items-start"
                  >
                    <div className="flex-1">
                      <p className="font-semibold text-black mb-0 mt-3 text-base">
                        {formatCourseName(item.courseName, item.brand)}
                      </p>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-gray-600 text-base">
                          Verified Certificate
                        </span>
                        <span className="text-black text-base font-normal">
                          {formatCurrency(item.price)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between items-center mb-4">
                  <span className="font-semibold text-base text-black">
                    Total
                  </span>
                  <div className="flex items-center">
                    {subtotal > total && (
                      <span className="text-gray-500 line-through text-sm mr-2">
                        {formatCurrency(subtotal)}USD
                      </span>
                    )}
                    <span className="font-semibold text-base text-black">
                      {formatCurrency(total)}
                    </span>
                  </div>
                </div>
                <MonarchDataConsumer<AbandonedCartTestResult>
                  experimentKey="useAbandonedCart"
                  render={(testResult) => {
                    const buttonText =
                      testResult?.data?.goToCartCtaText ?? "Go to cart";
                    return (
                      <Button
                        variant="brand"
                        className="w-full"
                        size="default"
                        onClick={() => handleCheckoutClick(buttonText)}
                      >
                        {buttonText}
                      </Button>
                    );
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {items.length === 0 && (
        <FeatureFlagComponentResolverClient
          flagKey="NEXT_PUBLIC_FEATURE_PRODUCT_VIEW_TRACKING"
          variants={{
            legacy: null,
            test: (
              <MonarchResolver<AbandonedCartIntentMatchTestResult>
                experimentKey="useAbandonedCartIntentMatch"
                variants={{
                  control: null,
                  test: (
                    <MonarchDataConsumer<AbandonedCartIntentMatchTestResult>
                      experimentKey="useAbandonedCartIntentMatch"
                      render={(testResult) => {
                        if (!testResult.data?.enabled) {
                          return null;
                        }
                        return (
                          <CartSuggestions
                            ctaText={testResult.data.cartCtaText ?? ""}
                          />
                        );
                      }}
                    />
                  ),
                }}
                Loader={null}
                fallbackVariant="control"
              />
            ),
          }}
          fallback="legacy"
        />
      )}
    </div>
  );
}
